import {
  Subscription,
  Plan,
  canAccessFeature,
  TierFeatureKey,
  subscriptionTypeIncludesAcademy,
  type SubscriptionTier,
} from '@court-sync/shared';
import { resolveCurrentSubscriptionTier } from './subscriptionTier';
import type { SubscriptionResponse } from '../services/subscription';

export interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  subscription: Subscription | null;
  isLoading: boolean;
  error: string | null;
  isTrialActive: boolean;
  isTrialExpired: boolean;
  daysLeftInTrial?: number;
  lastFetched: number | null;
  hasAcademyOwnerSubscription: boolean;
  hasUsedTrial: boolean;
  hasPaymentMethod: boolean;
}

const initialState: SubscriptionStatus = {
  hasActiveSubscription: false,
  subscription: null,
  isLoading: false,
  error: null,
  isTrialActive: false,
  isTrialExpired: false,
  lastFetched: null,
  hasAcademyOwnerSubscription: false,
  hasUsedTrial: false,
  hasPaymentMethod: false,
};

export function computeDerivedSubscriptionFlags(
  subscription: Subscription | null | undefined,
  plans: Plan[] | null | undefined
): { currentTier: SubscriptionTier; hasMultiLocation: boolean } {
  const currentTier = resolveCurrentSubscriptionTier(subscription, plans);
  return {
    currentTier,
    hasMultiLocation: canAccessFeature(currentTier, TierFeatureKey.MULTI_LOCATION),
  };
}

export function buildSubscriptionStatusFromResponse(
  response: SubscriptionResponse | null
): SubscriptionStatus {
  if (!response || !response.subscription) {
    return {
      ...initialState,
      subscription: null,
      hasActiveSubscription: false,
      hasAcademyOwnerSubscription: false,
      isLoading: false,
      lastFetched: Date.now(),
      hasUsedTrial: response?.hasUsedTrial || false,
      hasPaymentMethod: response?.hasPaymentMethod || false,
    };
  }

  return calculateSubscriptionStatus(
    response.subscription,
    response.hasUsedTrial || false,
    response.hasPaymentMethod || false
  );
}

export function getDisplayStatus(status: SubscriptionStatus): SubscriptionStatus {
  if (status.subscription && status.lastFetched && status.subscription.trialEnd) {
    const calculatedStatus = calculateSubscriptionStatus(
      status.subscription,
      status.hasUsedTrial,
      status.hasPaymentMethod
    );
    return { ...status, ...calculatedStatus };
  }
  return status;
}

function calculateSubscriptionStatus(
  subscription: Subscription,
  hasUsedTrial: boolean = false,
  hasPaymentMethod: boolean = false
): SubscriptionStatus {
  const subStatus = subscription.status;

  const hasActiveSubscription = subStatus === 'active' || subStatus === 'trialing';
  const isAcademyOwnerSubscription = subscriptionTypeIncludesAcademy(subscription.subscriptionType);

  const isTrialActive = subscription.isTrialActive || false;
  let isTrialExpired = false;
  let daysLeftInTrial: number | undefined;

  if (isTrialActive && subscription.trialEnd) {
    const trialEndDate =
      subscription.trialEnd instanceof Date
        ? subscription.trialEnd
        : new Date(subscription.trialEnd);
    const now = new Date();
    isTrialExpired = trialEndDate < now;

    if (!isTrialExpired) {
      daysLeftInTrial = Math.max(
        0,
        Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      );
    }
  }

  const shouldBlock = isTrialExpired && subStatus !== 'active';
  const hasAcademyOwnerSubscription =
    isAcademyOwnerSubscription && !shouldBlock && hasActiveSubscription;

  return {
    hasActiveSubscription: !shouldBlock && hasActiveSubscription,
    subscription,
    isLoading: false,
    error: null,
    isTrialActive,
    isTrialExpired,
    daysLeftInTrial,
    lastFetched: Date.now(),
    hasAcademyOwnerSubscription,
    hasUsedTrial,
    hasPaymentMethod,
  };
}
